import React, { FC, useEffect, useState } from 'react';

import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import styles from './ManagedUsersTable.module.scss';
import AutocompleteAssociationsTable from 'components/AutocompleteAssociationsTable/AutocompleteAssociationsTable';
import { BaseUserModel } from 'models/BaseUserModel';
import { UserService } from 'pages/UserListPage/UserService';
import { DetailedUserModel } from 'models/DetailedUserModel';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

interface ManagedUsersTableProps {
  detailedUser: DetailedUserModel;
  isLoading: boolean;
  onUserUpdate: () => void;
}

const ManagedUsersTable: FC<ManagedUsersTableProps> = (
  {
    detailedUser,
    isLoading,
    onUserUpdate
  }: ManagedUsersTableProps) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [displayedUsers, setDisplayedUsers] = useState<{id: string, email: string}[]>([]);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [managedUsers, setManagedUsers] = useState<string[]>([]);
  const [unmanagedUsers, setUnmanagedUsers] = useState<BaseUserModel[]>([]);
  const [users, setUsers] = useState<BaseUserModel[]>([])

  useEffect(() => {
    const getUsers = async (): Promise<void> => {
      try {
        const fetchedUsers: BaseUserModel[] = await UserService.getUsers();

        if (!fetchedUsers) {
          throw new Error('No users returned by the service.');
        }

        setUsers(fetchedUsers);
      } catch (error) {
        setUsers([]);
        console.error('Failed at getting users:', error);
        const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at getting users. Please refresh, and contact support if the issue persists.'};
        setCurrentAlert(alertConfig);
        setIsAlertDialogOpen(true);
      }
    };

    getUsers();

    const displayedUsers = detailedUser.manageUsers.map((email: string, index: number) => {
      return {
        id: index.toString(),
        email,
      }
    });

    setDisplayedUsers(displayedUsers);
    setManagedUsers(detailedUser.manageUsers);
  }, [detailedUser]);

  useEffect(() => {
    if (!users || !managedUsers) return;

    const unassociatedUsers = users.filter(user => !managedUsers.includes(user.email));
    setUnmanagedUsers(unassociatedUsers);
  }, [managedUsers, users]);

  const addManagedUserHandler = async (event: any, selectedUser: any): Promise<void> => {
    if (!selectedUser) return;

    try {
      const addManagedUserResult = await UserService.addOperatorForManager(detailedUser.email, [selectedUser.email]);

      if (!addManagedUserResult) {
        throw new Error('Error assigning user to manager.');
      }

      onUserUpdate();
    } catch (error) {
      console.error('Failed assigning user to manager.', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed assigning user to manager. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  };

  const removeManagedUserHandler = async (selectedUserEmail: string): Promise<void> => {
    if (!selectedUserEmail) return;

    try {
      const removeManagedUserResult = await UserService.removeOperatorFromManager(detailedUser.email, [selectedUserEmail]);

      if (!removeManagedUserResult) {
        throw new Error('Error removing user from manager');
      }

      onUserUpdate();
    } catch (error) {
      console.error('Failed removing user from manager.', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed removing user from manager. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  };

  const renderUserActions = (params: GridRenderCellParams): JSX.Element => {
    return (
      <IconButton
        color="error"
        onClick={() => removeManagedUserHandler(params.row.email)}
      >
        <DeleteIcon />
      </IconButton>
    )
  }

  const managedUserColumns: GridColDef[] = [
    { field: 'email', headerName: 'Email', flex: 2 },
    {
      field: 'actions',
      headerName: '',
      width: 40,
      renderCell: renderUserActions,
    },
  ];

  return (
    <div className={styles.ManagedUsersTable} data-testid="ManagedUsersTable">
      <AutocompleteAssociationsTable
        displayedItems={displayedUsers}
        unassociatedItems={unmanagedUsers}
        columns={managedUserColumns}
        isLoading={isLoading}
        onAddItem={addManagedUserHandler}
        getOptionLabel={(option) => option.email}
        title="Managed Users"
      />

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </div>
  );
}

export default ManagedUsersTable;
